<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>
<p-toast></p-toast>

<div class="p-fluid">
  <app-warehousing-page-header
    activeSection="reservations"
    title="Reservations"
    subtitle="Review inbound reservation demand and open a focused request workspace for unit allocation.">
    <div actions class="flex gap-2 flex-wrap">
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadReservations()"></p-button>
    </div>
  </app-warehousing-page-header>

  <div class="grid mb-3">
    <div class="col-12 md:col-3"><div class="warehousing-table-card h-full"><span class="text-500 font-semibold">Requests</span><h2 class="m-0">{{ totals.requestCount }}</h2></div></div>
    <div class="col-12 md:col-3"><div class="warehousing-table-card h-full"><span class="text-500 font-semibold">Requested</span><h2 class="m-0">{{ totals.requestedQuantity | number:'1.0-4' }}</h2></div></div>
    <div class="col-12 md:col-3"><div class="warehousing-table-card h-full"><span class="text-500 font-semibold">Reserved</span><h2 class="m-0">{{ totals.reservedQuantity | number:'1.0-4' }}</h2></div></div>
    <div class="col-12 md:col-3"><div class="warehousing-table-card h-full"><span class="text-500 font-semibold">Remaining</span><h2 class="m-0">{{ totals.remainingQuantity | number:'1.0-4' }}</h2></div></div>
  </div>

  <app-warehousing-search-panel title="Search Reservations" [resultCount]="requests.length" [totalCount]="requests.length" (search)="applySearch()" (clear)="clearSearch()">
    <div [formGroup]="searchForm" class="formgrid grid">
      <div class="field col-12 md:col-4">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="reservationKeyword" pInputText formControlName="keyword">
            <label for="reservationKeyword">Keyword</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select id="reservationWarehouse" [options]="warehouses" optionLabel="name" optionValue="id" formControlName="warehouseId" [showClear]="true" styleClass="w-full"></p-select>
            <label for="reservationWarehouse">Warehouse</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-check-circle"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select id="reservationCondition" [options]="conditions" optionLabel="name" optionValue="id" formControlName="conditionId" [showClear]="true" styleClass="w-full"></p-select>
            <label for="reservationCondition">Condition</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select id="reservationStatus" [options]="statusOptions" optionLabel="label" optionValue="value" formControlName="status" [showClear]="true" styleClass="w-full"></p-select>
            <label for="reservationStatus">Status</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select id="reservationReferenceType" [options]="referenceTypeOptions" optionLabel="label" optionValue="value" formControlName="referenceType" [showClear]="true" styleClass="w-full"></p-select>
            <label for="reservationReferenceType">Reference</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-history"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select id="reservationDeleted" [options]="recordOptions" optionLabel="label" optionValue="value" formControlName="deleted" styleClass="w-full"></p-select>
            <label for="reservationDeleted">Records</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
    </div>
  </app-warehousing-search-panel>

  <section class="warehousing-table-card">
    <div class="warehousing-table-header">
      <div>
        <h2>Reservation Directory</h2>
        <p>Open a request to allocate or release exact stock units in a focused workspace.</p>
      </div>
      <span class="warehousing-result-chip">{{ requests.length }} requests</span>
    </div>

    <p-table
      [value]="requests"
      [paginator]="true"
      [first]="first"
      [rows]="rows"
      (onPage)="onPage($event)"
      styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} requests"
      [rowsPerPageOptions]="[12, 25, 50]"
      [tableStyle]="{ 'min-width': '88rem' }">
      <ng-template pTemplate="header">
        <tr>
          <th>Reservation</th>
          <th>Item</th>
          <th>Warehouse</th>
          <th>Reservation Time</th>
          <th>Quantities</th>
          <th>Status</th>
          <th>Audit</th>
          <th class="text-right">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>
            <div class="stack-cell">
              <span class="warehousing-code-pill">{{ row.referenceNumber || ('REQ/' + (row.id || row.referenceLineId)) }}</span>
              <div class="cell-meta vertical">
                <span><i class="pi pi-briefcase"></i> {{ referenceTypeLabel(row.referenceType) }}</span>
                <span><i class="pi pi-file"></i> {{ row.referenceName || 'Operational reservation request' }}</span>
              </div>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <span class="cell-title">{{ row.itemName || '-' }}</span>
              <div class="cell-meta vertical">
                <span><i class="pi pi-hashtag"></i> {{ row.itemNumber || row.skuNumber || '-' }}</span>
                <span *ngIf="itemSizeLabel(row)"><i class="pi pi-expand"></i> {{ itemSizeLabel(row) }}</span>
                <span><i class="pi pi-box"></i> {{ allocationCount(row) }} allocated units</span>
              </div>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <span class="cell-title">{{ row.warehouseName || '-' }}</span>
              <div class="cell-meta vertical">
                <span><i class="pi pi-check-circle"></i> {{ row.conditionName || 'Any condition' }}</span>
                <span><i class="pi pi-link"></i> {{ row.referenceType || '-' }}</span>
              </div>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <span class="cell-title">{{ displayDateTime(reservationTime(row)) }}</span>
              <div class="cell-meta vertical">
                <span><i class="pi pi-clock"></i> {{ reservationTimeLabel(row) }}</span>
                <span><i class="pi pi-calendar"></i> Reserve by {{ row.requiredBy ? displayDate(row.requiredBy) : 'No deadline' }}</span>
                <span><i class="pi pi-comment"></i> {{ row.notes || 'No notes' }}</span>
              </div>
            </div>
          </td>
          <td>
            <div class="stack-cell number-stack">
              <div class="quantity-inline"><span>Req</span><strong>{{ row.requestedQuantity | number:'1.0-4' }}</strong></div>
              <div class="cell-meta vertical">
                <span>Reserved {{ (row.reservedQuantity || 0) | number:'1.0-4' }}</span>
                <span>Remaining {{ (row.remainingQuantity || 0) | number:'1.0-4' }}</span>
              </div>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <p-tag [value]="row.reservationStatus || 'PLANNED'" [severity]="reservationSeverity(row.reservationStatus)"></p-tag>
            </div>
          </td>
          <td><app-audit-directory-cell [stamp]="row"></app-audit-directory-cell></td>
          <td class="text-right">
            <div class="reservation-actions">
              <p-button label="Open" icon="pi pi-arrow-right" [routerLink]="['/warehousing/reservations/view', row.id || row.referenceLineId]"
                [queryParams]="reservationListQueryParams()"></p-button>
              @if (canViewOrigin(row)) {
                <p-button label="POSM" icon="pi pi-external-link" severity="secondary" [outlined]="true"
                  [routerLink]="['/posm/projects', row.referenceHeaderId]" [queryParams]="{ tab: 'materials' }"></p-button>
              }
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="8">
            <div class="warehousing-empty-state">
              <i class="pi pi-search"></i>
              <strong>No reservation requests found</strong>
              <span>No matching reservation requests are waiting in warehousing.</span>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </section>
</div>
